import * as zExt from "@redotech/rpc/ext";
import * as z from "zod";

const matchStrategy = z.enum([
  "variantId",
  "sku",
  "handleAndOptions",
  "titleAndOptions",
]);

const matchSpec = z.object({
  strategy: matchStrategy,
  /** Column holding the variant ID, SKU, handle, or title. */
  keyColumn: z.string(),
  /** Columns forming the within-product key. Empty targets a lone variant. */
  optionColumns: z.array(z.string()),
});

/** One CSV column routed to one metafield or one native variant field. */
const columnTarget = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("metafield"),
    column: z.string(),
    /** Which resource the metafield hangs off. */
    owner: z.enum(["variant", "product"]),
    namespace: z.string(),
    key: z.string(),
    type: z.string(),
    /** Blank cell deletes the metafield instead of being skipped. */
    clearBlanks: z.boolean().optional(),
  }),
  z.object({
    kind: z.literal("field"),
    column: z.string(),
    field: z.enum([
      "sku",
      "barcode",
      "price",
      "compareAtPrice",
      "cost",
      "weight",
    ]),
    /** Blank cell clears the field instead of being skipped. */
    clearBlanks: z.boolean().optional(),
  }),
]);

const columnGuess = z.object({
  column: z.string(),
  guessedType: z.string(),
  filledRows: z.number().int().nonnegative(),
  sampleValues: z.array(z.string()),
});

const planWarning = z.object({
  kind: z.enum([
    "unmatched",
    "ambiguous",
    "no-value",
    "type-mismatch",
    "variants-truncated",
    "gid-not-found",
    "product-conflict",
  ]),
  message: z.string(),
  count: z.number().int().nonnegative(),
  samples: z.array(z.string()),
});

const suspicion = z.object({
  kind: z.enum([
    "format-outlier",
    "cosmetic-only",
    "numeric-swing",
    "column-swap",
    "mass-overwrite",
  ]),
  label: z.string(),
  message: z.string(),
  count: z.number().int().nonnegative(),
  samples: z.array(z.string()),
});

const previewChange = z.object({
  variantGid: z.string(),
  productTitle: z.string(),
  variantTitle: z.string(),
  values: z.array(
    z.object({ label: z.string(), before: z.string(), after: z.string() }),
  ),
});

export const importCatalogCsv = {
  name: "importCatalogCsv" as const,
  input: z.object({
    teamId: zExt.objectId(),
    /** When true, resolve and diff only — nothing is written. */
    dryRun: z.boolean(),
    match: matchSpec.optional(),
    /** Empty on the first upload, when only headers and type guesses are wanted. */
    targets: z.array(columnTarget),
    ambiguityPolicy: z.enum(["skip", "all"]),
    /**
     * Forward-fills product-level columns down a Shopify export's variant rows.
     */
    forwardFillFromColumn: z.string().optional(),
    /**
     * Export every variant instead of planning an import. Needs no file: the
     * metafield columns are whatever the catalog turns out to carry.
     */
    exportCatalog: z.boolean().optional(),
  }),
  output: z.object({
    headers: z.array(z.string()),
    columnGuesses: z.array(columnGuess),
    rowsParsed: z.number().int().nonnegative(),
    rowsMatched: z.number().int().nonnegative(),
    rowsUnchanged: z.number().int().nonnegative(),
    variantsToChange: z.number().int().nonnegative(),
    /** Products whose product-level metafields will change. */
    productsToChange: z.number().int().nonnegative(),
    valuesToWrite: z.number().int().nonnegative(),
    previewChanges: z.array(previewChange),
    warnings: z.array(planWarning),
    /** Changes that look like mistakes. Advisory — they still write. */
    suspicions: z.array(suspicion),
    /**
     * Mappings that write where nothing reads: a metafield defined on the other
     * owner, or on neither. Checked against the store's own definitions.
     */
    ownershipNotes: z.array(
      z.object({
        kind: z.enum(["undefined-metafield", "owner-mismatch", "type-differs"]),
        message: z.string(),
        targetId: z.string(),
      }),
    ),
    /**
     * True once the run actually resolved and diffed. False for a headers-only
     * read, so the card does not present zero matches as a failed validate.
     */
    planned: z.boolean(),
    /** False for a dry run, or when the run stopped before writing. */
    applied: z.boolean(),
    valuesWritten: z.number().int().nonnegative(),
    valuesFailed: z.number().int().nonnegative(),
    /** Every matched variant as it stands now, keyed on variant ID. */
    snapshotCsv: z.string(),
    /** Variants the snapshot covers, matched whether or not they change. */
    snapshotRowCount: z.number().int().nonnegative(),
    /** Values this run creates from nothing; clearing must be on to undo them. */
    createdValueCount: z.number().int().nonnegative(),
    /** Populated only for an export run. */
    exportCsv: z.string(),
    exportVariantCount: z.number().int().nonnegative(),
    /** Variant metafield keys the export discovered, e.g. "custom.bin". */
    exportMetafieldKeys: z.array(z.string()),
    /** Variants whose metafields overflowed one page, so their row is partial. */
    exportTruncatedVariants: z.array(z.string()),
    errors: z.array(z.string()),
  }),
};
